/**
 * Weight conversion and display helpers.
 * API values are already in the user's selected unit; backend handles conversion.
 */
export const LBS_PER_KG = 2.20462;
export const KG_TO_LB = 2.20462;
export const LB_TO_KG = 0.45359237;

/**
 * Convert weight value between scale units (1 = KG, 2 = LBS).
 */
export const convertWeight = (
  value: number,
  fromUnit: number,
  toUnit: number,
): number => {
  if (fromUnit === toUnit || !Number.isFinite(value)) {
    return value;
  }

  // 1 = KG, 2 = LBS
  if (fromUnit === 1 && toUnit === 2) {
    return value * KG_TO_LB;
  }

  if (fromUnit === 2 && toUnit === 1) {
    return value * LB_TO_KG;
  }

  return value;
};

export type WeightUnitCode = 'kg' | 'lbs';

/** Unit tokens the weight screens store, mirroring `HEIGHT_UNIT`. */
export const WEIGHT_UNIT = {
  KG: 'kg',
  LBS: 'lbs',
} as const satisfies Record<string, WeightUnitCode>;

/** OnboardingProfileInfo WeightUnit enum (never send UI labels like "lbs" / "kg"). */
export type OnboardingWeightUnit = 'WeightKG' | 'WeightLBS';

export const ONBOARDING_WEIGHT_UNIT = {
  KG: 'WeightKG',
  LBS: 'WeightLBS',
} as const satisfies Record<string, OnboardingWeightUnit>;

/** Backend ScaleUnit lookup — Weight rows. */
export const WEIGHT_SCALE_UNIT_ID = {
  KG: 1,
  LBS: 2,
} as const;

export const WEIGHT_LIMITS = {
  [WEIGHT_SCALE_UNIT_ID.KG]: {
    min: 35,
    max: 453,
    label: 'kg',
  },
  [WEIGHT_SCALE_UNIT_ID.LBS]: {
    min: 77,
    max: 999,
    label: 'lbs',
  },
} as const;

export interface WeightValidationErrorItem {
  fieldKey: string;
  defaultFieldName: string;
  min: number;
  max: number;
  unit: string;
  message: string;
}

export interface WeightValidationResult {
  isValid: boolean;
  errors: WeightValidationErrorItem[];
  combinedErrorMessage?: string;
  normalizedStartWeight?: number;
  normalizedGoalWeight?: number;
}

import i18n from '../i18n';

/**
 * Format combined weight error message for multiple invalid weight fields.
 * Example: "Your Current Weight, Start Weight, and Goal Weight should be between 35 kg and 453 kg."
 */
export const formatCombinedWeightErrorMessage = (
  fieldNames: string[],
  unitId: number | string = WEIGHT_SCALE_UNIT_ID.LBS,
  t?: (key: string, options?: any) => string,
): string => {
  if (fieldNames.length === 0) return '';
  const resolvedUnitId =
    typeof unitId === 'number' ? unitId : toWeightScaleUnitId(unitId);
  const limits =
    WEIGHT_LIMITS[resolvedUnitId as keyof typeof WEIGHT_LIMITS] ??
    WEIGHT_LIMITS[WEIGHT_SCALE_UNIT_ID.LBS];

  let joinedFields = '';
  if (fieldNames.length === 1) {
    joinedFields = fieldNames[0];
  } else if (fieldNames.length === 2) {
    joinedFields = `${fieldNames[0]} and ${fieldNames[1]}`;
  } else {
    joinedFields = `${fieldNames.slice(0, -1).join(', ')}, and ${fieldNames[fieldNames.length - 1]}`;
  }

  const translate = t || ((key: string, options?: any) => i18n.t(key, options));
  const message = translate(
    'profile.updateProfile.errors.combinedWeightRangeError',
    {
      fields: joinedFields,
      min: limits.min,
      max: limits.max,
      unit: limits.label,
    },
  );

  if (typeof message === 'string' && message.length > 0) {
    return message;
  }

  return `Your ${joinedFields} should be between ${limits.min} ${limits.label} and ${limits.max} ${limits.label}.`;
};

/**
 * Validate an individual weight value against scale unit boundaries.
 */
export const validateWeight = (
  value: number | null | undefined,
  fieldName: string,
  selectedUnit: number,
): string | null => {
  const limits =
    WEIGHT_LIMITS[selectedUnit as keyof typeof WEIGHT_LIMITS] ??
    WEIGHT_LIMITS[WEIGHT_SCALE_UNIT_ID.LBS];

  if (value == null || !Number.isFinite(Number(value))) {
    return `${fieldName} should be between ${limits.min} ${limits.label} and ${limits.max} ${limits.label}.`;
  }
  const num = Number(value);

  if (num < limits.min || num > limits.max) {
    return `${fieldName} should be between ${limits.min} ${limits.label} and ${limits.max} ${limits.label}.`;
  }

  return null;
};

/**
 * Validate all profile weight fields (CurrentWeight, StartWeight, GoalWeight)
 * after normalizing start and goal weights to the selected WeightScaleUnit.
 */
export const validateProfileWeights = (params: {
  currentWeight?: number | null;
  startWeight?: number | null;
  startWeightUnit?: string | number | null;
  goalWeight?: number | null;
  goalWeightUnit?: string | number | null;
  unit?: string | number | null;
  t?: (key: string, options?: any) => string;
}): WeightValidationResult => {
  const resolveUnitId = (unit?: string | number | null): number => {
    if (typeof unit === 'number') return unit;
    return toWeightScaleUnitId(unit != null ? String(unit) : null);
  };

  const targetUnitId = resolveUnitId(params.unit);
  const startUnitId = resolveUnitId(params.startWeightUnit || params.unit);
  const goalUnitId = resolveUnitId(params.goalWeightUnit || params.unit);

  const normalizedStartWeight =
    params.startWeight != null && Number.isFinite(Number(params.startWeight))
      ? convertWeight(Number(params.startWeight), startUnitId, targetUnitId)
      : undefined;

  const normalizedGoalWeight =
    params.goalWeight != null && Number.isFinite(Number(params.goalWeight))
      ? convertWeight(Number(params.goalWeight), goalUnitId, targetUnitId)
      : undefined;

  const limits =
    WEIGHT_LIMITS[targetUnitId as keyof typeof WEIGHT_LIMITS] ??
    WEIGHT_LIMITS[WEIGHT_SCALE_UNIT_ID.LBS];

  const errors: WeightValidationErrorItem[] = [];

  const checkValue = (
    val: number | null | undefined,
    fieldKey: string,
    defaultFieldName: string,
  ) => {
    const num = val != null ? Number(val) : NaN;
    if (!Number.isFinite(num) || num < limits.min || num > limits.max) {
      errors.push({
        fieldKey,
        defaultFieldName,
        min: limits.min,
        max: limits.max,
        unit: limits.label,
        message: `Should be between ${limits.min} ${limits.label} and ${limits.max} ${limits.label}.`,
      });
    }
  };

  checkValue(
    params.currentWeight,
    'profile.updateProfile.labels.currentWeight',
    'Current Weight',
  );
  if (params.startWeight != null) {
    checkValue(
      normalizedStartWeight,
      'profile.updateProfile.labels.startWeight',
      'Start Weight',
    );
  }
  if (params.goalWeight != null) {
    checkValue(
      normalizedGoalWeight,
      'profile.updateProfile.labels.goalWeight',
      'Goal Weight',
    );
  }

  const combinedErrorMessage =
    errors.length > 0
      ? formatCombinedWeightErrorMessage(
          errors.map((e) => e.defaultFieldName),
          targetUnitId,
          params.t,
        )
      : undefined;

  return {
    isValid: errors.length === 0,
    errors,
    combinedErrorMessage,
    normalizedStartWeight:
      normalizedStartWeight != null
        ? Number(normalizedStartWeight.toFixed(2))
        : undefined,
    normalizedGoalWeight:
      normalizedGoalWeight != null
        ? Number(normalizedGoalWeight.toFixed(2))
        : undefined,
  };
};

export const normalizeWeightUnit = (unit?: string | null): WeightUnitCode => {
  const u = String(unit ?? '')
    .toLowerCase()
    .trim();
  if (
    u === 'lbs' ||
    u === 'lb' ||
    u === 'weightlbs' ||
    u === '2' ||
    u.includes('lb')
  ) {
    return 'lbs';
  }
  return 'kg';
};

export const DEFAULT_WEIGHT_KG = 35;
export const DEFAULT_WEIGHT_LBS = 77;

/** Default weight value for picker initialization based on minimum of scale unit (35.0 kg / 77.0 lbs). */
export const getDefaultWeightForUnit = (unit?: string | null): number => {
  const u = normalizeWeightUnit(unit);
  return u === 'lbs' ? DEFAULT_WEIGHT_LBS : DEFAULT_WEIGHT_KG;
};

/** Map UI weight preference → OnboardingProfileInfo WeightUnit enum. */
export const toOnboardingWeightUnit = (
  unit?: string | null,
): OnboardingWeightUnit =>
  normalizeWeightUnit(unit) === 'lbs'
    ? ONBOARDING_WEIGHT_UNIT.LBS
    : ONBOARDING_WEIGHT_UNIT.KG;

/** Map UI / preference unit → ScaleUnitID (1 = KG, 2 = LBS). */
export const toWeightScaleUnitId = (unit?: string | null): number =>
  normalizeWeightUnit(unit) === 'lbs'
    ? WEIGHT_SCALE_UNIT_ID.LBS
    : WEIGHT_SCALE_UNIT_ID.KG;

/** Convert lbs → kg (3 decimal places, matches API examples e.g. 65.770). */
export const lbsToKgPrecise = (lbs: number): number =>
  Number((lbs / LBS_PER_KG).toFixed(3));

/** Convert kg → lbs for display (nearest whole pound). */
export const kgToLbsPrecise = (kg: number): number =>
  Math.round(kg * LBS_PER_KG);

/**
 * Resolve a weight value to kilograms (legacy helper for callers that still need it).
 */
export const toWeightKg = (value: number, unit?: string | null): number => {
  if (!Number.isFinite(value)) return 0;
  return normalizeWeightUnit(unit) === 'lbs'
    ? lbsToKgPrecise(value)
    : Number(Number(value).toFixed(3));
};

/**
 * Convert a kilogram value into the user's preferred display unit.
 */
export const fromWeightKg = (
  kg: number,
  displayUnit?: string | null,
): number => {
  if (!Number.isFinite(kg)) return 0;
  return normalizeWeightUnit(displayUnit) === 'lbs'
    ? kgToLbsPrecise(kg)
    : Number(Number(kg).toFixed(1));
};

/**
 * Format a value that is already in the preferred display unit.
 */
export const formatWeightInUnit = (
  value: number | null | undefined,
  unit?: string | null,
): string => {
  if (value == null || !Number.isFinite(Number(value))) return '';
  const u = normalizeWeightUnit(unit);
  const num = Number(value);
  const formatted = num.toFixed(1);
  if (u === 'lbs') {
    return `${formatted} lbs`;
  }
  return `${formatted} kg`;
};

/**
 * Format weight for UI. Value is already in the preferred unit (backend converts).
 * Example: (145, 'lbs') → "145 lbs"
 */
export const formatWeightDisplay = (
  weight: number | null | undefined,
  unit?: string | null,
): string => formatWeightInUnit(weight, unit);

/** Integer helpers used by buddy-search sliders (legacy). */
export const kgToLbs = (kg: number): number => Math.round(kg * LBS_PER_KG);

export const lbsToKg = (lbs: number): number => Math.round(lbs / LBS_PER_KG);

/**
 * Calculates the weight progress percentage based on start, current, and goal weight.
 */
export const calculateWeightProgress = (
  startWeight?: number | null,
  currentWeight?: number | null,
  goalWeight?: number | null,
): number => {
  if (startWeight == null || currentWeight == null || goalWeight == null) {
    return 0;
  }

  const totalToLose = startWeight - goalWeight;
  const lostWeight = startWeight - currentWeight;

  if (totalToLose === 0) {
    return currentWeight <= goalWeight ? 100 : 0;
  }

  return Math.max(0, Math.min(100, (lostWeight / totalToLose) * 100));
};
